// PerfumesPage.jsx
import { useTranslation } from "react-i18next";
import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, Star, SlidersHorizontal, ChevronDown, ChevronRight,
  ArrowRight, Search, Heart, X, Truck, RotateCcw, Award,
  ZoomIn, ZoomOut, Gem, Move, Menu, Loader, MessageSquare, Send, Loader2
} from "lucide-react";
import SEO from "./SEO";

/* ─── API Base URL ─── */
const API_BASE = import.meta.env.VITE_API_URL || 'https://daroulmesbackend.onrender.com';

const INSPECT_MODAL_RESPONSIVE_CSS = `
@media (max-width: 760px) {
  .inspect-modal-overlay {
    align-items: stretch !important;
    justify-content: flex-start !important;
    padding: max(10px, env(safe-area-inset-top)) 10px max(10px, env(safe-area-inset-bottom)) !important;
    overflow-y: auto !important;
  }
  .inspect-modal-shell {
    width: 100% !important;
    max-width: none !important;
    height: calc(100dvh - 20px) !important;
    max-height: calc(100dvh - 20px) !important;
    border-radius: 16px !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .inspect-modal-header {
    padding: 12px 14px !important;
    gap: 10px !important;
  }
  .inspect-modal-header h3,
  .inspect-modal-header p {
    max-width: 58vw !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }
  .inspect-modal-tabs,
  .inspect-modal-controls-row {
    width: 100% !important;
    order: 3 !important;
    justify-content: stretch !important;
  }
  .inspect-modal-tabs button,
  .inspect-modal-controls-row button {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    padding: 8px 8px !important;
    font-size: 10px !important;
  }
  .inspect-modal-body {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column !important;
    overflow-y: auto !important;
    min-height: 0 !important;
  }
  .inspect-modal-image-panel {
    flex: 0 0 auto !important;
    width: 100% !important;
    min-height: 0 !important;
    height: min(54dvh, 420px) !important;
  }
  .inspect-modal-image-panel > div {
    min-height: 0 !important;
    height: 100% !important;
  }
  .inspect-modal-image-panel img {
    object-fit: contain !important;
  }
  .inspect-modal-side {
    width: 100% !important;
    max-height: none !important;
    flex: 1 1 auto !important;
    border-left: none !important;
    border-top: 1px solid rgba(200,160,101,.18) !important;
    overflow-y: visible !important;
  }
  .inspect-modal-side > div {
    padding: 14px !important;
  }
  .inspect-modal-side [style*="flex-direction:column"] {
    flex-direction: row !important;
    flex-wrap: wrap !important;
  }
  .inspect-mobile-hide {
    display: none !important;
  }
}
@media (max-width: 420px) {
  .inspect-modal-image-panel {
    height: 46dvh !important;
  }
  .inspect-modal-header h3,
  .inspect-modal-header p {
    max-width: 48vw !important;
  }
}
`;
const formatMAD = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0 MAD";
  return `${amount.toLocaleString("en-MA", { minimumFractionDigits: amount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })} MAD`;
};

const TAG_LABELS = { bestseller: "Bestseller", new: "New", sale: "Sale" };
const SORT_LABELS = {
  featured: "Featured",
  price_low_high: "Price: Low to High",
  price_high_low: "Price: High to Low",
  top_rated: "Top Rated",
  newest: "Newest",
};

// Use only the origin (protocol+host+port) for static files — never any /api path
const STATIC_BASE = (() => { try { return new URL(API_BASE).origin; } catch { return 'https://daroulmesbackend.onrender.com'; } })();

/* ─── Helper to transform product with metadata ─── */
const transformProduct = (product) => {
  const metadata = product.productMetadata || [];
  const getMeta = (key) => metadata.find(m => m.key === key)?.value || null;

  // Parse size: could be a comma-separated string or multiple entries
  let sizeArray = [];
  const sizeValue = getMeta('size');
  if (sizeValue) {
    sizeArray = sizeValue.split(',').map(s => s.trim());
  }

  return {
    id: product.id,
    name: product.name,
    price: product.price,
    orig: product.origin ? parseFloat(product.origin) : null,
    rating: Math.round(product.rating || 0),
    reviews: product._count?.reviews ?? product.reviews?.length ?? 0,
    tag: product.tag || '',
    cat: product.category?.name || 'Uncategorized',
    style: getMeta('style') || 'Other',
    size: sizeArray.length ? sizeArray : ['One Size'],
    color: getMeta('color') || null,
    img:  product.imageUrl
               ? product.imageUrl.startsWith("http")
                 ? product.imageUrl
                 : `${API_BASE}/api/images/uploads/${product.imageUrl.split("/").pop()}`
               : null,
    material: getMeta('material') || null,
  };
};

/* ─── Stars Component ─── */
function Stars({ n = 5, size = 13 }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} fill={i < n ? "#c8a065" : "#e2d8ca"} color={i < n ? "#c8a065" : "#e2d8ca"} />
      ))}
    </span>
  );
}

/* ─── Accordion Filter ─── */
function AccordionFilter({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: "1px solid rgba(200,160,101,.18)", paddingBottom: 18, marginBottom: 18 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "transparent", border: "none", cursor: "pointer", padding: "0 0 14px 0",
      }}>
        <span style={{
          fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700,
          letterSpacing: ".14em", color: "#1f3d3a"
        }}>{title}</span>
        <ChevronDown size={14} color="#c8a065"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .3s" }} />
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

const SORT_OPTS = ["featured", "price_low_high", "price_high_low", "top_rated", "newest"];

/* ─── Interactive Star Input ─── */
function StarInput({ value, onChange, size = 24 }) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div style={{ display: "inline-flex", gap: 4 }} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHover(i)}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 2,
            transition: "transform .15s",
            transform: hover === i ? "scale(1.15)" : "scale(1)",
          }}
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
        >
          <Star
            size={size}
            fill={i <= display ? "#c8a065" : "transparent"}
            color={i <= display ? "#c8a065" : "#d4c5b0"}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

/* ─── Reviews Section (fetches + displays + submits) ─── */
function ReviewsSection({ productId }) {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // form state
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // auth detection — adjust the key if yours differs
  const token =
    (typeof window !== "undefined" &&
      (localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        "")) ||
    "";
  const currentUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/products/${productId}/reviews`);
      if (!res.ok) throw new Error("Failed to load reviews");
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : []);
      // if the user already reviewed, pre-fill their existing review
      if (currentUser) {
        const mine = data.find((r) => r.userId === currentUser.id);
        if (mine) {
          setRating(mine.rating);
          setComment(mine.comment || "");
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [productId, currentUser?.id]);

  useEffect(() => {
    if (productId) loadReviews();
  }, [productId, loadReviews]);

  const userAlreadyReviewed =
    currentUser && reviews.some((r) => r.userId === currentUser.id);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitError(null);
    setSubmitSuccess(false);
    if (!rating) { setSubmitError(t("reviews.error_rating", "Please pick a rating")); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/reviews`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ productId, rating, comment: comment.trim() || undefined }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload.error || payload.message || `HTTP ${res.status}`);
      }
      setSubmitSuccess(true);
      setComment("");
      setRating(0);
      // refresh the list so the new review appears
      loadReviews();
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : "0.0";

  const initials = (name) =>
    (name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
    } catch { return ""; }
  };

  return (
    <div style={{ padding: "20px 18px", borderTop: "1px solid rgba(200,160,101,.14)" }}>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MessageSquare size={14} color="#1f3d3a" />
          <p style={{
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
            letterSpacing: ".12em", color: "#1f3d3a", margin: 0,
          }}>
            {t("reviews.title", "REVIEWS")}
          </p>
        </div>
        {reviews.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Stars n={Math.round(avg)} size={12} />
            <span style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              color: "#7a6a58", letterSpacing: ".05em",
            }}>
              {avg} · {reviews.length}
            </span>
          </div>
        )}
      </div>

      {/* List */}
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 20, color: "#b0a090" }}>
          <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
        </div>
      )}
      {error && (
        <div style={{
          padding: "10px 12px", borderRadius: 8, background: "#fef2f2", color: "#991b1b",
          fontSize: 12, fontFamily: "'Montserrat',sans-serif",
        }}>
          {error}
        </div>
      )}

      {!loading && !error && reviews.length === 0 && (
        <div style={{
          padding: "18px 16px", textAlign: "center", borderRadius: 10,
          background: "rgba(200,160,101,.06)",
          fontFamily: "'Cormorant Garamond',serif", fontSize: 14, color: "#7a6a58", fontStyle: "italic",
        }}>
          {t("reviews.empty", "No reviews yet. Be the first to share your thoughts.")}
        </div>
      )}

      {!loading && reviews.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 240, overflowY: "auto", paddingRight: 4 }}>
          {reviews.map((r) => (
            <div key={r.id} style={{
              padding: "12px 14px", borderRadius: 10,
              background: "#fff", border: "1px solid rgba(200,160,101,.18)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "linear-gradient(135deg,#1f3d3a,#2d5550)",
                  color: "#dfcc25",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                }}>
                  {initials(r.user?.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Cormorant Garamond',serif", fontSize: 14, fontWeight: 600,
                    color: "#1f3d3a", lineHeight: 1.2,
                  }}>
                    {r.user?.name || t("reviews.anonymous", "Anonymous")}
                  </div>
                  <div style={{
                    fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "#b0a090",
                    letterSpacing: ".05em",
                  }}>
                    {formatDate(r.createdAt)}
                  </div>
                </div>
                <Stars n={Math.round(r.rating)} size={11} />
              </div>
              {r.comment && (
                <p style={{
                  margin: 0, fontFamily: "'Cormorant Garamond',serif",
                  fontSize: 14, lineHeight: 1.55, color: "#4a3d2f",
                }}>
                  {r.comment}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Write form */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px dashed rgba(200,160,101,.25)" }}>
        {!token && (
          <p style={{
            margin: 0, padding: "12px 14px", borderRadius: 10,
            background: "rgba(31,61,58,.05)",
            fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
            color: "#1f3d3a", fontStyle: "italic", textAlign: "center",
          }}>
            {t("reviews.login_required", "Please log in to write a review.")}
          </p>
        )}

        {token && userAlreadyReviewed && (
          <p style={{
            margin: 0, padding: "12px 14px", borderRadius: 10,
            background: "rgba(200,160,101,.08)",
            fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
            color: "#7a6a58", fontStyle: "italic", textAlign: "center",
          }}>
            {t("reviews.already_reviewed", "You've already reviewed this product. Thank you!")}
          </p>
        )}

        {token && !userAlreadyReviewed && (
          <form onSubmit={submit}>
            <p style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: ".12em", color: "#1f3d3a", margin: "0 0 10px",
            }}>
              {t("reviews.write_title", "SHARE YOUR EXPERIENCE")}
            </p>

            <div style={{ marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10,
                color: "#b0a090", letterSpacing: ".1em",
              }}>
                {t("reviews.your_rating", "YOUR RATING")}
              </span>
              <StarInput value={rating} onChange={setRating} size={22} />
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("reviews.placeholder", "Tell others what you think about this piece…")}
              rows={3}
              maxLength={1000}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "10px 12px", borderRadius: 10,
                border: "1px solid rgba(200,160,101,.35)",
                background: "#fdfaf5", outline: "none", resize: "vertical",
                fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
                color: "#1f3d3a", lineHeight: 1.5,
              }}
              onFocus={e => e.currentTarget.style.borderColor = "#1f3d3a"}
              onBlur={e => e.currentTarget.style.borderColor = "rgba(200,160,101,.35)"}
            />

            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 10, gap: 10, flexWrap: "wrap",
            }}>
              <span style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10,
                color: "#b0a090", letterSpacing: ".05em",
              }}>
                {comment.length}/1000
              </span>
              <button
                type="submit"
                disabled={submitting || !rating}
                style={{
                  padding: "10px 18px", borderRadius: 10,
                  background: submitting || !rating ? "#9ca3a0" : "#1f3d3a",
                  border: "none",
                  fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600,
                  letterSpacing: ".1em", color: "#fdf8f2",
                  cursor: submitting || !rating ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  transition: "background .2s",
                }}
                onMouseEnter={e => { if (!submitting && rating) e.currentTarget.style.background = "#2d5550"; }}
                onMouseLeave={e => { if (!submitting && rating) e.currentTarget.style.background = "#1f3d3a"; }}
              >
                {submitting
                  ? <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} />
                  : <Send size={13} />}
                {t("reviews.submit", "POST REVIEW")}
              </button>
            </div>

            {submitError && (
              <p style={{
                marginTop: 10, padding: "8px 12px", borderRadius: 8,
                background: "#fef2f2", color: "#991b1b",
                fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              }}>
                {submitError}
              </p>
            )}
            {submitSuccess && (
              <p style={{
                marginTop: 10, padding: "8px 12px", borderRadius: 8,
                background: "#f0fdf4", color: "#166534",
                fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              }}>
                {t("reviews.success", "Thank you for your review!")}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

/* ─── Product Zoom / Fragrance Viewer Modal ─── */
function ZoomModal({ product, onClose }) {
  const containerRef = useRef(null);
  const { t } = useTranslation(); 
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [lensPos, setLensPos] = useState({ x: -200, y: -200 });
  const [lensVisible, setLensVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("zoom");
  const dragStart = useRef({ x: 0, y: 0, px: 50, py: 50 });
  const LENS_SIZE = 150;
  const LENS_ZOOM = 4;

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => Math.min(6, Math.max(1, z - e.deltaY * 0.005)));
  }, []);

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
  };

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const lx = e.clientX - rect.left;
    const ly = e.clientY - rect.top;
    setLensPos({ x: lx, y: ly });

    if (isDragging) {
      const dx = ((e.clientX - dragStart.current.x) / rect.width) * 100 / zoom;
      const dy = ((e.clientY - dragStart.current.y) / rect.height) * 100 / zoom;
      setPos({
        x: Math.min(100, Math.max(0, dragStart.current.px - dx)),
        y: Math.min(100, Math.max(0, dragStart.current.py - dy)),
      });
    }
  }, [isDragging, zoom]);

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  const zoomLevels = [1, 1.5, 2, 3, 4, 6];

  return (
    <div className="inspect-modal-overlay" style={{
      position:"fixed",inset:0,zIndex:1000,
      background:"rgba(10,18,15,.85)",
      backdropFilter:"blur(16px)",
      display:"flex",alignItems:"center",justifyContent:"center",
      padding:20,
    }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      
      <style>{INSPECT_MODAL_RESPONSIVE_CSS}</style>
      <div className="inspect-modal-shell" style={{
        background:"#fdf8f2",borderRadius:22,overflow:"hidden",
        width:"min(920px,96vw)",maxHeight:"92vh",
        display:"flex",flexDirection:"column",
        boxShadow:"0 50px 120px rgba(0,0,0,.55)",
        border:"1px solid rgba(200,160,101,.35)",
      }}>
        {/* Header */}
        <div className="inspect-modal-header" style={{
          display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"18px 26px",
          borderBottom:"1px solid rgba(200,160,101,.18)",
          background:"rgba(255,253,249,.98)",
          flexWrap:"wrap",gap:10,
        }}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{
              width:38,height:38,borderRadius:"50%",
              background:"linear-gradient(135deg,#1f3d3a,#2d5550)",
              display:"flex",alignItems:"center",justifyContent:"center",
            }}>
              <span style={{fontSize:20}}>🌸</span>
            </div>
            <div>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600,
                color:"#1f3d3a",margin:0,lineHeight:1.2}}>{product.name}</h3>
              <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"#9a8a78",margin:0,
                letterSpacing:".1em"}}>{t("zoom.fragrance_inspector", "Fragrance Inspector")}</p>
            </div>
          </div>

          {/* Mode tabs */}
          <div className="inspect-modal-tabs" style={{display:"flex",background:"rgba(31,61,58,.07)",borderRadius:10,padding:4,gap:4}}>
            {[["zoom","🔬 Zoom Pan"],["lens","🌸 Scent Detail"]].map(([mode,label])=>(
              <button key={mode} onClick={()=>setActiveTab(mode)} style={{
                padding:"7px 16px",borderRadius:8,border:"none",cursor:"pointer",
                fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:600,letterSpacing:".06em",
                background:activeTab===mode?"#1f3d3a":"transparent",
                color:activeTab===mode?"#fdf8f2":"#7a6a58",
                transition:"all .2s",
              }}>{label}</button>
            ))}
          </div>

          <button onClick={onClose} style={{
            width:36,height:36,borderRadius:"50%",border:"1.5px solid rgba(192,83,58,.4)",
            background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
            transition:"all .2s",flexShrink:0,
          }}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(192,83,58,.12)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <X size={15} color="#c0533a"/>
          </button>
        </div>

        {/* Viewer area */}
        <div className="inspect-modal-body" style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}}>

          {/* Main image panel */}
          <div style={{flex:1,position:"relative",overflow:"hidden",background:"#111"}}>
            {activeTab === "zoom" ? (
              <div
                ref={containerRef}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                style={{
                  width:"100%",height:"100%",minHeight:440,overflow:"hidden",
                  cursor: zoom>1 ? (isDragging?"grabbing":"grab") : "zoom-in",
                  position:"relative",userSelect:"none",
                }}
              >
                <img
                  src={product.img}
                  alt={product.name}
                  draggable={false}
                  style={{
                    width:"100%",height:"100%",objectFit:"cover",
                    transformOrigin:`${pos.x}% ${pos.y}%`,
                    transform:`scale(${zoom})`,
                    transition: isDragging?"none":"transform 0.15s ease",
                    display:"block",
                  }}
                />
                {/* Soft perfume glow effect at high zoom */}
                {zoom >= 3 && (
                  <div style={{
                    position:"absolute",inset:0,pointerEvents:"none",
                    background:"radial-gradient(circle at 30% 20%, rgba(200,160,101,.08), transparent 60%)",
                  }}/>
                )}
                <div style={{
                  position:"absolute",bottom:16,left:16,
                  background:"rgba(0,0,0,.55)",borderRadius:8,
                  padding:"6px 14px",backdropFilter:"blur(6px)",
                  display:"flex",alignItems:"center",gap:8,
                }}>
                  <span style={{
                    fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,
                    color:"#c8a065",letterSpacing:".06em",
                  }}>{zoom.toFixed(1)}×</span>
                  {zoom>=3 && <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"rgba(255,255,255,.7)"}}>{t("collectionPage.fragrance_detail", "Bottle detail")}</span>}
                </div>
                {zoom > 1 && (
                  <div style={{
                    position:"absolute",
                    left:`${pos.x}%`,top:`${pos.y}%`,
                    transform:"translate(-50%,-50%)",
                    pointerEvents:"none",opacity:0.5,
                  }}>
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <line x1="14" y1="0" x2="14" y2="28" stroke="#c8a065" strokeWidth="1.5"/>
                      <line x1="0" y1="14" x2="28" y2="14" stroke="#c8a065" strokeWidth="1.5"/>
                      <circle cx="14" cy="14" r="5" fill="none" stroke="#c8a065" strokeWidth="1.5"/>
                    </svg>
                  </div>
                )}
                <div className="inspect-mobile-hide" style={{
                  position:"absolute",bottom:16,right:16,
                  fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"rgba(255,255,255,.6)",
                  letterSpacing:".06em",textAlign:"right",pointerEvents:"none",
                }}>
                  {zoom>1?t("zoom.drag_to_pan"):t("zoom.scroll_to_zoom")}
                </div>
              </div>
            ) : (
              /* Fragrance Detail Lens Mode */
              <div
                ref={containerRef}
                onMouseMove={()=>setLensVisible(true)}
                onMouseLeave={()=>setLensVisible(false)}
                style={{
                  width:"100%",height:"100%",minHeight:440,
                  position:"relative",overflow:"hidden",cursor:"none",userSelect:"none",
                }}
              >
                <img
                  src={product.img}
                  // src={product.img}
                  alt={product.name}
                  draggable={false}
                  style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                />
                {/* Soft perfume glow overlay */}
                <div style={{
                  position:"absolute",inset:0,
                  background:"radial-gradient(circle at 25% 25%, rgba(200,160,101,.06), transparent 50%)",
                  pointerEvents:"none",
                }}/>
                {/* Lens circle */}
                {lensVisible && (
                  <div style={{
                    position:"absolute",
                    left: lensPos.x - LENS_SIZE/2,
                    top:  lensPos.y - LENS_SIZE/2,
                    width: LENS_SIZE,
                    height: LENS_SIZE,
                    borderRadius:"50%",
                    overflow:"hidden",
                    border:"3px solid #c8a065",
                    boxShadow:"0 0 0 1px rgba(200,160,101,.5), 0 0 20px rgba(200,160,101,.3), 0 10px 40px rgba(0,0,0,.6)",
                    pointerEvents:"none",
                    zIndex:10,
                  }}>
                    {containerRef.current && (() => {
                      const rect = containerRef.current.getBoundingClientRect();
                      const bgPosX = LENS_SIZE/2 - lensPos.x * LENS_ZOOM;
                      const bgPosY = LENS_SIZE/2 - lensPos.y * LENS_ZOOM;
                      return (
                        <div style={{
                          position:"absolute",
                          inset:0,
                          backgroundImage:`url(${product.img})`,
                          backgroundSize:`${rect.width * LENS_ZOOM}px ${rect.height * LENS_ZOOM}px`,
                          backgroundPosition:`${bgPosX}px ${bgPosY}px`,
                          backgroundRepeat:"no-repeat",
                        }}/>
                      );
                    })()}
                    {/* Lens crosshair */}
                    <div style={{
                      position:"absolute",inset:0,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      pointerEvents:"none",opacity:0.4,
                    }}>
                      <svg width={LENS_SIZE} height={LENS_SIZE} viewBox={`0 0 ${LENS_SIZE} ${LENS_SIZE}`}>
                        <line x1={LENS_SIZE/2} y1="0" x2={LENS_SIZE/2} y2={LENS_SIZE} stroke="#c8a065" strokeWidth="1"/>
                        <line x1="0" y1={LENS_SIZE/2} x2={LENS_SIZE} y2={LENS_SIZE/2} stroke="#c8a065" strokeWidth="1"/>
                        <circle cx={LENS_SIZE/2} cy={LENS_SIZE/2} r={LENS_SIZE/2-2} fill="none" stroke="#c8a065" strokeWidth="1.5"/>
                        <circle cx={LENS_SIZE/2} cy={LENS_SIZE/2} r={8} fill="none" stroke="#c8a065" strokeWidth="1"/>
                      </svg>
                    </div>
                    {/* Gold shimmer rim */}
                    <div style={{
                      position:"absolute",inset:0,borderRadius:"50%",
                      background:"linear-gradient(135deg,rgba(255,255,255,.18) 0%,transparent 50%)",
                      pointerEvents:"none",
                    }}/>
                  </div>
                )}
                {/* Lens handle */}
                {lensVisible && (
                  <div style={{
                    position:"absolute",
                    left:lensPos.x+LENS_SIZE/2*0.65,top:lensPos.y+LENS_SIZE/2*0.65,
                    width:32,height:6,background:"linear-gradient(90deg,#c8a065,#dfcc25)",borderRadius:3,
                    transform:"rotate(45deg)",transformOrigin:"0 50%",
                    pointerEvents:"none",zIndex:11,
                    boxShadow:"0 2px 8px rgba(0,0,0,.5)",
                  }}/>
                )}
                <div className="inspect-mobile-hide" style={{
                  position:"absolute",bottom:16,right:16,
                  fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"rgba(255,255,255,.6)",
                  letterSpacing:".06em",pointerEvents:"none",
                }}>{t("zoom.inspect_fragrance", { zoom: LENS_ZOOM, defaultValue: `Inspect details at ${LENS_ZOOM}×` })}</div>
              </div>
            )}
          </div>

          {/* Right panel */}
          <div className="inspect-modal-side" style={{
            width:300,flexShrink:0,background:"rgba(255,253,249,.98)",
            borderLeft:"1px solid rgba(200,160,101,.18)",
            display:"flex",flexDirection:"column",overflowY:"auto",
          }}>
            {activeTab === "zoom" && (
              <div style={{padding:"20px 18px",borderBottom:"1px solid rgba(200,160,101,.14)"}}>
                <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,
                  letterSpacing:".12em",color:"#1f3d3a",marginBottom:14}}>{t("collectionPage.zoom_level")}</p>
                <div style={{display:"flex",flexDirection:"column",gap:7}}>
                  {zoomLevels.map(z=>(
                    <button key={z} onClick={()=>{setZoom(z);setPos({x:50,y:50});}} style={{
                      padding:"8px 0",borderRadius:8,border:"1.5px solid",
                      borderColor:Math.abs(zoom-z)<0.1?"#1f3d3a":"rgba(200,160,101,.4)",
                      background:Math.abs(zoom-z)<0.1?"#1f3d3a":"transparent",
                      color:Math.abs(zoom-z)<0.1?"#fdf8f2":"#7a6a58",
                      fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:600,
                      cursor:"pointer",transition:"all .2s",
                    }}>
                      {z===1?"Original":`${z}×`}{z>=4?" — Bottle Detail":""}
                    </button>
                  ))}
                </div>
                <div style={{
                  marginTop:14,display:"flex",justifyContent:"space-between",
                  background:"rgba(31,61,58,.06)",borderRadius:8,padding:4,
                }}>
                  <button onClick={()=>setZoom(z=>Math.max(1,+(z-0.25).toFixed(2)))} style={{
                    flex:1,padding:"7px 0",border:"none",background:"transparent",
                    cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                    borderRadius:6,transition:"background .15s",
                  }}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(200,160,101,.14)"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <ZoomOut size={16} color="#c8a065"/>
                  </button>
                  <button onClick={()=>setZoom(z=>Math.min(6,+(z+0.25).toFixed(2)))} style={{
                    flex:1,padding:"7px 0",border:"none",background:"transparent",
                    cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                    borderRadius:6,transition:"background .15s",
                  }}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(200,160,101,.14)"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <ZoomIn size={16} color="#c8a065"/>
                  </button>
                </div>
              </div>
            )}

            {activeTab === "lens" && (
              <div style={{padding:"20px 18px",borderBottom:"1px solid rgba(200,160,101,.14)"}}>
                <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,
                  letterSpacing:".12em",color:"#1f3d3a",marginBottom:10}}>{t("collectionPage.scent_lens", "SCENT DETAIL LENS")}</p>
                <p style={{
                  fontFamily: "'Montserrat',sans-serif",
                  fontSize: 12,
                  fontWeight: 300,
                  color: "#7a6a58",
                  lineHeight: 1.7,
                  margin: 0
                }}>
                  {t("product_detail.inspect_fragrance_detail", { zoom: LENS_ZOOM, defaultValue: "Move over the image to inspect the bottle, label, cap, and texture details." })}
                </p>
                <div style={{
                  marginTop:14,padding:"12px 14px",
                  background:"linear-gradient(135deg,rgba(31,61,58,.07),rgba(200,160,101,.08))",
                  borderRadius:10,border:"1px solid rgba(200,160,101,.25)",
                }}>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:"#7a6a58",lineHeight:1.7}}>
                    🌸 Bottle finish<br/>✨ Cap detail<br/>🔖 Label clarity<br/>🎨 Color depth
                  </span>
                </div>
              </div>
            )}

            {/* Product details */}
            <div style={{padding:"20px 18px"}}>
              <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,
                letterSpacing:".12em",color:"#1f3d3a",marginBottom:14}}>{t("collectionPage.details")}</p>
              {[
                [t("product_detail.material"), product.material],
                ["COLOR",    product.color],
                [t("product_detail.category"), product.cat],
                [t("product_detail.style"),    product.style],
                [t("product_detail.price"),    formatMAD(product.price)],
              ].filter(([, val]) => val).map(([label,val])=>(
                <div key={label} style={{marginBottom:12,paddingBottom:12,borderBottom:"1px solid rgba(200,160,101,.1)"}}>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:"#b0a090",letterSpacing:".1em",display:"block",marginBottom:3}}>{label}</span>
                  <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:16,color:"#1f3d3a",fontWeight:500}}>{val}</span>
                </div>
              ))}
            </div>

            {/* Reviews */}
            <ReviewsSection productId={product.id} />

            <div style={{padding:"16px 18px",borderTop:"1px solid rgba(200,160,101,.14)"}}>
              <button style={{
                width:"100%",padding:"12px 0",borderRadius:10,background:"#1f3d3a",border:"none",
                fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:600,letterSpacing:".1em",
                color:"#fdf8f2",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                transition:"background .2s",
              }}
                onMouseEnter={e=>e.currentTarget.style.background="#2d5550"}
                onMouseLeave={e=>e.currentTarget.style.background="#1f3d3a"}>
                <ShoppingCart size={15}/>{t("product.add_to_cart")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════ MAIN PAGE ═══════════════════════════════════════ */
/* ═══════════════════════════════════════ MAIN PAGE ═══════════════════════════════════════ */
export default function PerfumesPage() {
  const { t } = useTranslation();
  // Data state
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [activeCat, setActiveCat] = useState("Perfumes");
  const [activeStyles, setActiveStyles] = useState([]);
  const [activeSizes, setActiveSizes] = useState([]);
  const [maxPrice, setMaxPrice] = useState(1200);
  const [activeTags, setActiveTags] = useState([]);
  const [sortBy, setSortBy] = useState("featured");
  const [search, setSearch] = useState("");
  const [wishlist, setWishlist] = useState([]);
  const [cartAdded, setCartAdded] = useState([]);
  const [modalZoom, setModalZoom] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const navigate = useNavigate();

useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      const [catRes, prodRes] = await Promise.all([
        fetch(`${API_BASE}/api/categories`),
        fetch(`${API_BASE}/api/products?includeMetadata=true&includeReviews=true&limit=1000`)
      ]);

      if (!catRes.ok) throw new Error(`Categories failed: ${catRes.status}`);
      if (!prodRes.ok) throw new Error(`Products failed: ${prodRes.status}`);

      const catData = await catRes.json();
      const prodData = await prodRes.json();

      setCategories(catData);
      // ⚠️ Important: API returns { data: [...], pagination: {...} }
      const transformed = prodData.data.map(transformProduct);
      setProducts(transformed);
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);

  // Derive filter options from products
  const allStyles = useMemo(() => {
    const set = new Set(products.map(p => p.style).filter(Boolean));
    return Array.from(set).sort();
  }, [products]);

  const allSizes = useMemo(() => {
    const set = new Set();
    products.forEach(p => p.size.forEach(s => set.add(s)));
    return Array.from(set).sort((a, b) => {
      // Natural sort for sizes (e.g., "5", "6", "10", "16\"", "One Size")
      const aNum = parseFloat(a);
      const bNum = parseFloat(b);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.localeCompare(b);
    });
  }, [products]);

  const maxProductPrice = useMemo(() => {
    return products.length ? Math.max(...products.map(p => p.price)) : 1200;
  }, [products]);

  // Update maxPrice when products load
  useEffect(() => {
    if (products.length > 0) {
      setMaxPrice(maxProductPrice);
    }
  }, [maxProductPrice, products.length]);

  const allTags = useMemo(() => {
    const set = new Set(products.map(p => p.tag).filter(Boolean));
    return Array.from(set);
  }, [products]);

  // Toggle handlers
  const toggleStyle = s => setActiveStyles(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  const toggleSize = s => setActiveSizes(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  const toggleTag = t => setActiveTags(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);
  const toggleWish = id => setWishlist(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const addCart = id => {
    const product = products.find(p => p.id === id);
    if (!product) return;

    const stored = localStorage.getItem("dar_oulmes_cart");
    const cart = stored ? JSON.parse(stored) : [];
    const existing = cart.find(item => item.id === id);
    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({ id: product.id, name: product.name, price: product.price, img: product.img, tag: product.tag || "", quantity: 1 });
    }
    localStorage.setItem("dar_oulmes_cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cart-updated"));

    setCartAdded(p => [...p, id]);
    setTimeout(() => setCartAdded(p => p.filter(x => x !== id)), 1400);
  };

  const hasFilters = activeCat !== "All" || activeStyles.length || activeSizes.length || activeTags.length || search || maxPrice < maxProductPrice;
  const clearAll = () => {
    setActiveCat("All");
    setActiveStyles([]);
    setActiveSizes([]);
    setActiveTags([]);
    setMaxPrice(maxProductPrice);
    setSearch("");
  };

  // Filtered and sorted products
  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCat !== "All") list = list.filter(p => p.cat === activeCat);
    if (activeStyles.length) list = list.filter(p => activeStyles.includes(p.style));
    if (activeSizes.length) list = list.filter(p => p.size.some(s => activeSizes.includes(s)));
    if (activeTags.length) list = list.filter(p => activeTags.includes(p.tag));
    list = list.filter(p => p.price <= maxPrice);
    if (search) list = list.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

    // Sorting
    switch (sortBy) {
      case "price_low_high": list.sort((a, b) => a.price - b.price); break;
      case "price_high_low": list.sort((a, b) => b.price - a.price); break;
      case "top_rated": list.sort((a, b) => b.rating - a.rating); break;
      case "newest": list.sort((a, b) => b.id - a.id); break; // Sort by id descending as proxy
      default: break;
    }
    return list;
  }, [products, activeCat, activeStyles, activeSizes, activeTags, maxPrice, search, sortBy]);

  // Category options for filter
  const categoryOptions = useMemo(() => {
    return ["All", ...categories.map(c => c.name)];
  }, [categories]);

  // Count products per category for display
  const categoryCounts = useMemo(() => {
    const counts = { All: products.length };
    categories.forEach(cat => {
      counts[cat.name] = products.filter(p => p.cat === cat.name).length;
    });
    return counts;
  }, [products, categories]);

  const SORT_KEYS = ["featured","price_low_high","price_high_low","top_rated","newest"];
  const TAG_STYLE = {
    Bestseller: { background: "#1f3d3a", color: "#dfcc25" },
    New: { background: "#c8a065", color: "#fff" },
    Sale: { background: "#c0533a", color: "#fff" },
  };

  const SidebarContent = () => (
    <>
      <SEO
        title={t("collectionPage.seo_perfumes", "Perfumes in Oulmès | Dar Oulmès")} 
        description="Shop elegant perfumes in Oulmès including fresh, floral, oriental, and premium fragrance picks at reasonable prices from Dar Oulmès."
        canonical="/perfumes"
        image="/seo/perfumes.jpg"
      />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <SlidersHorizontal size={15} color="#c8a065" />
          <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: ".14em", color: "#1f3d3a" }}>{t("collectionPage.filters_title")}</span>
        </div>
        {hasFilters && <button className="cp-clear-btn" onClick={clearAll}>{"CLEAR ALL"}</button>}
      </div>

      <AccordionFilter title={t("filters.category")}>
        {categoryOptions.map(c => (
          <button key={c} className={`cp-cat${activeCat === c ? " active" : ""}`} onClick={() => setActiveCat(c)}>
            <span>{c}</span>
            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#c8a065", fontWeight: 400 }}>
              {categoryCounts[c] || 0}
            </span>
          </button>
        ))}
      </AccordionFilter>

      <AccordionFilter title={t("filters.price_range")}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "#7a6a58" }}>
          <span>{formatMAD(0)}</span><span style={{ color: "#1f3d3a", fontWeight: 600 }}>{formatMAD(maxPrice)}</span>
        </div>
        <input type="range" min={0} max={maxProductPrice} value={maxPrice} onChange={e => setMaxPrice(+e.target.value)} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#b0a090" }}>
          <span>{formatMAD(0)}</span><span>{formatMAD(maxProductPrice)}</span>
        </div>
      </AccordionFilter>

      {allStyles.length > 0 && (
        <AccordionFilter title={t("filters.style")}>
          {allStyles.map(s => (
            <label key={s} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11, cursor: "pointer" }}>
              <div className={`cp-mbox${activeStyles.includes(s) ? " on" : ""}`} onClick={() => toggleStyle(s)}>
                {activeStyles.includes(s) && <span style={{ color: "#fdf8f2", fontSize: 10, fontWeight: 700 }}>✓</span>}
              </div>
              <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 300, color: "#5a4a38", flex: 1 }}>{s}</span>
              <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, color: "#c8a065" }}>
                {products.filter(p => p.style === s).length}
              </span>
            </label>
          ))}
        </AccordionFilter>
      )}

      {allSizes.length > 0 && (
        <AccordionFilter title={t("filters.size")}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {allSizes.map(s => (
              <button key={s} className={`cp-size${activeSizes.includes(s) ? " active" : ""}`} onClick={() => toggleSize(s)}>{s}</button>
            ))}
          </div>
        </AccordionFilter>
      )}
      
      {allTags.length > 0 && (
        <AccordionFilter title={t("filters.collection")}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 12,
                  padding: "6px 14px",
                  borderRadius: 30,
                  border: "1px solid rgba(200,160,101,0.3)",
                  background: activeTags.includes(tag) ? "#c8a065" : "transparent",
                  color: activeTags.includes(tag) ? "#fff" : "#1f3d3a",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {tag}
              </button>
            ))}
          </div>
        </AccordionFilter>
      )}

      {/* Fragrance inspector callout */}
      <div style={{ marginTop: 4, padding: "16px", background: "linear-gradient(135deg,rgba(31,61,58,.07),rgba(200,160,101,.08))", borderRadius: 12, border: "1px solid rgba(200,160,101,.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
          <span style={{ fontSize: 16 }}>🌸</span>
          <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".12em", color: "#1f3d3a" }}>{t("collectionPage.fragrance_inspector", "FRAGRANCE INSPECTOR")}</span>
        </div>
        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 300, color: "#7a6a58", lineHeight: 1.7, margin: 0 }}>
          {t("collectionPage.hover_fragrance_prefix", "Use")} <strong style={{ color: "#c8a065" }}>{t("collectionPage.inspect", "inspect")}</strong> {t("collectionPage.hover_fragrance_suffix", "to zoom into bottle details before ordering.")}
        </p>
      </div>

      <button className="cp-reset-btn" onClick={clearAll}>{"RESET ALL"}</button>
    </>
  );

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', fontFamily: "'Montserrat',sans-serif", color: '#1f3d3a' }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite', marginRight: 12 }} />
        {"Loading perfumes..."}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', fontFamily: "'Montserrat',sans-serif", color: '#c0533a' }}>
        <X size={24} style={{ marginRight: 8 }} />
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", background: "#fdf8f2", minHeight: "100vh", position: "relative", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300&family=Montserrat:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .cp-deco{position:fixed;inset:0;pointer-events:none;z-index:0;overflow:hidden;}
        .cp-card{background:#fff;border-radius:18px;overflow:hidden;border:1px solid rgba(200,160,101,.14);
          box-shadow:0 6px 22px rgba(180,140,100,.07);
          transition:transform .35s cubic-bezier(.34,1.56,.64,1),box-shadow .35s;
          cursor:pointer;position:relative;}
        .cp-card:hover{transform:translateY(-7px);box-shadow:0 28px 60px rgba(180,140,100,.22);}
        .cp-card:hover .cp-img{transform:scale(1.06);}
        .cp-img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .55s ease;}
        .cp-cat{width:100%;text-align:left;padding:10px 14px;border-radius:9px;border:none;
          font-family:'Montserrat',sans-serif;font-size:12px;font-weight:500;letter-spacing:.05em;
          cursor:pointer;transition:all .22s;background:transparent;color:#7a6a58;display:flex;justify-content:space-between;align-items:center;}
        .cp-cat.active{background:linear-gradient(135deg,rgba(31,61,58,.1),rgba(200,160,101,.14));color:#1f3d3a;font-weight:700;}
        .cp-cat:hover:not(.active){background:rgba(200,160,101,.09);color:#1f3d3a;}
        .cp-mbox{width:17px;height:17px;border-radius:4px;border:1.5px solid rgba(200,160,101,.5);
          display:flex;align-items:center;justify-content:center;transition:all .2s;flex-shrink:0;cursor:pointer;}
        .cp-mbox.on{background:#1f3d3a;border-color:#1f3d3a;}
        .cp-size{padding:7px 12px;border-radius:7px;border:1.5px solid rgba(200,160,101,.4);
          font-family:'Montserrat',sans-serif;font-size:11px;font-weight:600;letter-spacing:.06em;
          cursor:pointer;transition:all .22s;background:transparent;color:#7a6a58;}
        .cp-size.active{background:#1f3d3a;border-color:#1f3d3a;color:#fdf8f2;}
        .cp-size:hover:not(.active){background:rgba(200,160,101,.12);border-color:#c8a065;color:#1f3d3a;}
        .cp-chip{padding:7px 15px;border-radius:999px;border:1.5px solid rgba(200,160,101,.4);
          font-family:'Montserrat',sans-serif;font-size:12px;font-weight:500;letter-spacing:.06em;
          cursor:pointer;transition:all .22s;background:transparent;color:#7a6a58;}
        .cp-chip.active{background:#1f3d3a;border-color:#1f3d3a;color:#fdf8f2;}
        .cp-chip:hover:not(.active){background:rgba(200,160,101,.12);border-color:#c8a065;color:#1f3d3a;}
        .cp-select{font-family:'Montserrat',sans-serif;font-size:12px;color:#1f3d3a;
          border:1.5px solid rgba(200,160,101,.35);border-radius:9px;padding:9px 14px;background:#fff;cursor:pointer;outline:none;}
        .cp-search{display:flex;align-items:center;gap:11px;padding:12px 18px;
          background:#fff;border:1.5px solid rgba(200,160,101,.3);border-radius:12px;transition:border-color .2s,box-shadow .2s;}
        .cp-search:focus-within{border-color:#c8a065;box-shadow:0 0 0 3px rgba(200,160,101,.12);}
        .cp-cart{width:38px;height:38px;border-radius:50%;background:#1f3d3a;border:none;
          display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;
          transition:background .2s,transform .2s;}
        .cp-cart:hover{background:#2d5550;transform:scale(1.1);}
        .cp-cart.done{background:#c8a065;}
        .cp-wish{position:absolute;top:12px;right:12px;width:34px;height:34px;border-radius:50%;
          background:rgba(255,253,249,.92);border:1px solid rgba(200,160,101,.3);
          display:flex;align-items:center;justify-content:center;cursor:pointer;transition:transform .2s,background .2s;}
        .cp-wish:hover{transform:scale(1.12);background:#fff;}
        .cp-zoom-btn{position:absolute;bottom:0;left:0;right:0;
          padding:11px 0;background:linear-gradient(135deg,#1f3d3a,#2d5550);
          border:none;font-family:'Montserrat',sans-serif;font-size:11px;font-weight:700;
          letter-spacing:.14em;color:#dfcc25;cursor:pointer;display:flex;align-items:center;
          justify-content:center;gap:8px;transform:translateY(100%);transition:transform .32s cubic-bezier(.34,1.56,.64,1);}
        .cp-card:hover .cp-zoom-btn{transform:translateY(0);}
        input[type=range]{width:100%;accent-color:#c8a065;cursor:pointer;}
        .cp-pill{padding:4px 12px;border-radius:999px;background:rgba(200,160,101,.15);
          font-family:'Montserrat',sans-serif;font-size:12px;color:#7a6a58;display:flex;align-items:center;gap:5px;}
        .cp-clear-btn{font-family:'Montserrat',sans-serif;font-size:11px;color:#c8a065;
          background:none;border:none;cursor:pointer;letter-spacing:.06em;font-weight:500;padding:0;}
        .cp-reset-btn{width:100%;padding:12px;border:1.5px solid rgba(200,160,101,.4);border-radius:9px;
          background:transparent;font-family:'Montserrat',sans-serif;font-size:12px;font-weight:500;
          letter-spacing:.1em;color:#9a8a78;cursor:pointer;transition:all .25s;margin-top:6px;}
        .cp-reset-btn:hover{border-color:#1f3d3a;color:#1f3d3a;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);}}
        .cp-fu{animation:fadeUp .6s ease both;}
        .cp-grid>.cp-card:nth-child(1){animation:fadeUp .5s .04s ease both;}
        .cp-grid>.cp-card:nth-child(2){animation:fadeUp .5s .09s ease both;}
        .cp-grid>.cp-card:nth-child(3){animation:fadeUp .5s .14s ease both;}
        .cp-grid>.cp-card:nth-child(4){animation:fadeUp .5s .19s ease both;}
        .cp-grid>.cp-card:nth-child(5){animation:fadeUp .5s .24s ease both;}
        .cp-grid>.cp-card:nth-child(6){animation:fadeUp .5s .29s ease both;}
        .cp-grid>.cp-card:nth-child(n+7){animation:fadeUp .5s .34s ease both;}
        .cp-explore{padding:14px 32px;border-radius:7px;background:#c8a065;border:none;
          font-family:'Montserrat',sans-serif;font-size:12px;font-weight:600;letter-spacing:.12em;
          color:#1f3d3a;cursor:pointer;display:flex;align-items:center;gap:8px;
          box-shadow:0 6px 20px rgba(200,160,101,.4);transition:all .3s;white-space:nowrap;}
        .cp-explore:hover{background:#d4ae78;transform:translateY(-1px);}
        .cp-sidebar-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:200;}
        .cp-sidebar-drawer{position:fixed;left:0;top:0;bottom:0;width:min(300px,85vw);
          background:#fdf8f2;z-index:201;transform:translateX(-100%);transition:transform .35s cubic-bezier(.34,1.56,.64,1);
          overflow-y:auto;padding:26px 20px;border-right:1px solid rgba(200,160,101,.22);}
        .cp-sidebar-drawer.open{transform:translateX(0);}

        @media(max-width:1024px){
          .cp-layout{grid-template-columns:1fr!important;}
          .cp-sidebar-desktop{display:none!important;}
          .cp-mobile-filter-btn{display:flex!important;}
          .cp-grid{grid-template-columns:repeat(2,1fr)!important;}
          .cp-sidebar-overlay{display:block;}
        }
        @media(max-width:640px){
          .cp-grid{grid-template-columns:1fr!important;}
          .cp-trust-grid{grid-template-columns:1fr 1fr!important;}
          .cp-bottom-banner{flex-direction:column!important;text-align:center;}
        }
        @media(max-width:400px){
          .cp-trust-grid{grid-template-columns:1fr!important;}
        }
        .cp-mobile-filter-btn{display:none;}
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      {/* Deco */}
      <div className="cp-deco">
        <svg width="100%" height="100%" viewBox="0 0 1200 1900" preserveAspectRatio="xMaxYMin slice">
          {[0, 30, 60, 90, 120, 150, 180].map((r, i) => (
            <line key={`tr${i}`} x1="1200" y1="0"
              x2={1200 - 460 * Math.cos(r * Math.PI / 180)} y2={460 * Math.sin(r * Math.PI / 180)}
              stroke="#c8a065" strokeWidth=".7" strokeOpacity=".22" />
          ))}
          {[0, 30, 60, 90, 120, 150, 180].map((r, i) => (
            <line key={`bl${i}`} x1="0" y1="1900"
              x2={460 * Math.cos(r * Math.PI / 180)} y2={1900 - 460 * Math.sin(r * Math.PI / 180)}
              stroke="#c8a065" strokeWidth=".7" strokeOpacity=".22" />
          ))}
        </svg>
      </div>

      {/* Zoom Modal */}
      {modalZoom && <ZoomModal product={modalZoom} onClose={() => setModalZoom(null)} />}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="cp-sidebar-overlay" onClick={() => setSidebarOpen(false)}>
          <div className={`cp-sidebar-drawer${sidebarOpen ? " open" : ""}`} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
              <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                <X size={18} color="#7a6a58" />
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1260, margin: "0 auto", padding: "0 24px 90px" }}>

        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "28px 0 20px", fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "#9a8a78", letterSpacing: ".04em" }}>
          {[t("breadcrumb.home"), t("breadcrumb.collection"), "Perfumes"].map((c, i, a) => (
            <React.Fragment key={c}>
              <span style={{ cursor: i < a.length - 1 ? "pointer" : "default", color: i === a.length - 1 ? "#1f3d3a" : "#9a8a78" }}>{c}</span>
              {i < a.length - 1 && <ChevronRight size={12} color="#bbb" />}
            </React.Fragment>
          ))}
        </div>

        {/* Hero */}
        <div className="cp-fu" style={{ marginBottom: 16 }}>
          <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(38px,5vw,62px)", fontWeight: 400, color: "#1f3d3a", lineHeight: 1.15, marginBottom: 10 }}>
            {t("collectionPage.title_perfumes", "Perfumes Collection")}
          </h1>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 300, color: "#7a6a58", letterSpacing: ".04em", marginBottom: 28 }}>
            {products.length} curated pieces — crafted for moments that matter
          </p>
        </div>

        {/* Search + mobile filter */}
        <div style={{ display: "flex", gap: 12, marginBottom: 36, flexWrap: "wrap" }}>
          <div className="cp-fu cp-search" style={{ flex: 1, minWidth: 240 }}>
            <Search size={16} color="#c8a065" style={{ flexShrink: 0 }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={"Search perfumes..."}
              style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "#1f3d3a" }} />
            {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
              <X size={14} color="#9a8a78" />
            </button>}
          </div>
          <button className="cp-mobile-filter-btn" onClick={() => setSidebarOpen(true)} style={{
            alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 12,
            border: "1.5px solid rgba(200,160,101,.4)", background: "#fff", cursor: "pointer",
            fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: ".08em", color: "#1f3d3a",
          }}>
            <SlidersHorizontal size={15} color="#c8a065" />{t("collectionPage.filters_title")}
            {hasFilters && <span style={{ background: "#c8a065", color: "#fff", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
              {[activeCat !== "All", activeStyles.length > 0, activeSizes.length > 0, activeTags.length > 0, search, maxPrice < maxProductPrice].filter(Boolean).length}
            </span>}
          </button>
        </div>

        {/* Layout */}
        <div className="cp-layout" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 36, alignItems: "start" }}>

          {/* ════ SIDEBAR (desktop) ════ */}
          <aside className="cp-sidebar-desktop cp-fu" style={{
            background: "rgba(255,253,249,.96)", border: "1px solid rgba(200,160,101,.22)",
            borderRadius: 18, padding: "28px 22px",
            boxShadow: "0 8px 36px rgba(180,140,100,.09)",
            position: "sticky", top: 24,
          }}>
            <SidebarContent />
          </aside>

          {/* ════ GRID ════ */}
          <div>
            {/* Sort bar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "#7a6a58", fontWeight: 300, margin: 0 }}>
                {t("collectionPage.showing_pieces", { count: filtered.length, total: products.length })}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#9a8a78", letterSpacing: ".1em" }}>{t("collectionPage.sort_by")}</span>
                <select className="cp-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  {SORT_OPTS.map(o => <option key={o} value={o}>{SORT_LABELS[o] || o}</option>)}
                </select>
              </div>
            </div>

            {/* Active pills */}
            {hasFilters && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                {activeCat !== "All" && (
                  <span className="cp-pill" style={{ background: "rgba(31,61,58,.1)", color: "#1f3d3a" }}>
                    {activeCat}<X size={11} style={{ cursor: "pointer" }} onClick={() => setActiveCat("All")} />
                  </span>
                )}
                {activeStyles.map(s => (
                  <span key={s} className="cp-pill">
                    {s}<X size={11} style={{ cursor: "pointer" }} onClick={() => toggleStyle(s)} />
                  </span>
                ))}
                {activeSizes.map(s => (
                  <span key={s} className="cp-pill">
                    Size {s}<X size={11} style={{ cursor: "pointer" }} onClick={() => toggleSize(s)} />
                  </span>
                ))}
                {activeTags.map(t => (
                  <span key={t} className="cp-pill">
                    {t}<X size={11} style={{ cursor: "pointer" }} onClick={() => toggleTag(t)} />
                  </span>
                ))}
                {maxPrice < maxProductPrice && (
                  <span className="cp-pill">
                    Under {formatMAD(maxPrice)}<X size={11} style={{ cursor: "pointer" }} onClick={() => setMaxPrice(maxProductPrice)} />
                  </span>
                )}
                {search && (
                  <span className="cp-pill">
                    “{search}”<X size={11} style={{ cursor: "pointer" }} onClick={() => setSearch("")} />
                  </span>
                )}
              </div>
            )}

            {/* Product grid */}
            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "90px 0", fontFamily: "'Cormorant Garamond',serif", fontSize: 26, color: "#9a8a78", fontStyle: "italic" }}>
                {"No products found."}
              </div>
            ) : (
              <div className="cp-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 22 }}>
                {filtered.map(p => (
                  <div key={p.id} className="cp-card" onClick={() => navigate(`/product/${p.id}`)} style={{cursor:"pointer"}}>
                    {/* Image */}
                    <div style={{ position: "relative", aspectRatio: "3/4", overflow: "hidden", background: "#f0e8d8" }}>
                    
                      <img src={p.img} alt={p.name} className="cp-img" />
                      {p.tag && TAG_STYLE[p.tag] && (
                        <span style={{
                          position: "absolute", top: 13, left: 13, ...TAG_STYLE[p.tag],
                          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                          letterSpacing: ".1em", padding: "5px 11px", borderRadius: 4
                        }}>{p.tag}</span>
                      )}
                      {p.orig && (
                        <span style={{
                          position: "absolute", bottom: 54, left: 13,
                          background: "rgba(192,83,58,.92)", color: "#fff",
                          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                          padding: "4px 9px", borderRadius: 4
                        }}>
                          -{Math.round((1 - p.price / p.orig) * 100)}%
                        </span>
                      )}
                      <button className="cp-wish" onClick={e => { e.stopPropagation(); toggleWish(p.id); }}>
                        <Heart size={15}
                          fill={wishlist.includes(p.id) ? "#c0533a" : "none"}
                          color={wishlist.includes(p.id) ? "#c0533a" : "#9a8a78"} />
                      </button>
                      {/* Inspect / Zoom button */}
                      <button className="cp-zoom-btn" onClick={e => { e.stopPropagation(); setModalZoom(p); }}>
                        <span style={{ fontSize: 14 }}>🌸</span> {t("collectionPage.inspect_detail", "View Detail")}
                      </button>
                    </div>

                    {/* Info */}
                    <div style={{ padding: "17px 16px 15px", display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={{
                        fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 500,
                        color: "#c8a065", letterSpacing: ".08em"
                      }}>{p.material && p.material !== 'N/A' ? `${p.material} · ` : ''}{p.cat}</span>
                      <h4 style={{
                        fontFamily: "'Cormorant Garamond',serif", fontSize: 17, fontWeight: 500,
                        color: "#1f3d3a", lineHeight: 1.35, margin: 0
                      }}>{p.name}</h4>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <Stars n={p.rating} />
                        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#9a8a78" }}>
                          {p.rating > 0 ? p.rating.toFixed(1) : "—"}
                        </span>
                        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#9a8a78" }}>
                          ({p.reviews ? `${p.reviews} review${p.reviews !== 1 ? 's' : ''}` : 'No reviews'})
                        </span>
                      </div>
                      {/* Sizes */}
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {p.size.map(s => (
                          <span key={s} style={{
                            padding: "3px 8px", borderRadius: 5,
                            border: "1px solid rgba(200,160,101,.3)",
                            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 600,
                            color: "#7a6a58", letterSpacing: ".08em"
                          }}>{s}</span>
                        ))}
                      </div>
                      {/* Price + cart */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 600, color: "#1f3d3a" }}>{formatMAD(p.price)}</span>
                          {p.orig && (
                            <span style={{
                              fontFamily: "'Montserrat',sans-serif", fontSize: 12,
                              color: "#b0a090", textDecoration: "line-through"
                            }}>{formatMAD(p.orig)}</span>
                          )}
                        </div>
                        <button className={`cp-cart${cartAdded.includes(p.id) ? " done" : ""}`} onClick={e => { e.stopPropagation(); addCart(p.id); }}>
                          {cartAdded.includes(p.id)
                            ? <span style={{ fontSize: 16, color: "#fff", lineHeight: 1 }}>✓</span>
                            : <ShoppingCart size={15} color="#fdf8f2" />}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Trust strip */}
        <div className="cp-trust-grid" style={{
          marginTop: 76, display: "grid", gridTemplateColumns: "repeat(4,1fr)",
          background: "rgba(255,253,249,.94)", border: "1px solid rgba(200,160,101,.18)",
          borderRadius: 18, overflow: "hidden", boxShadow: "0 6px 30px rgba(180,140,100,.07)",
        }}>
          {[
            { icon: "🌸", title: "AUTHENTIC SCENTS", desc: "Carefully selected &\nlong-lasting" },
            { icon: "🔨", title: t("features.handcrafted"), desc: "Artisan-made with\nattention to detail" },
            { Icon: Truck, title: t("features.free_delivery"), desc: t("features.free_delivery_desc") },
            { Icon: RotateCcw, title: "30-DAY RETURNS", desc: "Hassle-free return\npolicy" },
          ].map(({ icon, Icon, title, desc }, i, a) => (
            <div key={title} style={{
              display: "flex", alignItems: "center", gap: 16, padding: "28px 20px",
              borderRight: i < a.length - 1 ? "1px solid rgba(200,160,101,.18)" : "none"
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: "50%", border: "1.5px solid rgba(200,160,101,.45)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                fontSize: icon ? 22 : undefined
              }}>
                {icon ? icon : <Icon size={19} color="#c8a065" />}
              </div>
              <div>
                <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".12em", color: "#1f3d3a", marginBottom: 4 }}>{title}</p>
                <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 300, color: "#7a6a58", lineHeight: 1.7, whiteSpace: "pre-line", margin: 0 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom banner */}
        <div className="cp-bottom-banner" style={{
          marginTop: 28, background: "#1f3d3a", borderRadius: 16, padding: "38px 46px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 32
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <div style={{
              width: 58, height: 58, borderRadius: "50%", border: "1.5px solid rgba(200,160,101,.5)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 30
            }}>🌸</div>
            <div>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 500, color: "#fdf8f2", marginBottom: 8, lineHeight: 1.2 }}>{t("collectionPage.perfume_banner_title", "Find your signature scent")}</h3>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 300, color: "rgba(253,248,242,.65)", lineHeight: 1.8, maxWidth: 400, margin: 0 }}>
                {t("collectionPage.perfume_banner_sub", "Discover perfumes that feel premium, memorable, and perfect for everyday confidence.")}
              </p>
            </div>
          </div>
          <button className="cp-explore">{t("features.explore_perfumes", "Explore perfumes")} <ArrowRight size={15} /></button>
        </div>

      </div>
    </div>
  );
}